import { useState } from "react";
import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import {
  LuCheck,
  LuCircleCheck,
  LuCircleOff,
  LuCopyPlus,
  LuPencil,
  LuPlus,
  LuSave,
  LuSearch,
  LuTag,
  LuTrash2,
} from "react-icons/lu";
import { createColumnHelper } from "@tanstack/react-table";
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Card,
  CloseButton,
  ColorPicker,
  Dialog,
  Field,
  Flex,
  Group,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  NativeSelect,
  parseColor,
  Portal,
  Stack,
  Text,
  Textarea,
  Toggle,
} from "@chakra-ui/react";
import { DataTable } from "../components/DataTable";
import { LoadingCard } from "../components/LoadingCard";
import { PageHeader } from "../components/PageHeader";
import { DrawerSidebar } from "../components/DrawerSidebar";
import { useViewerAccess } from "@/lib/access";
import {
  ActivityAdminAccessDenied,
  ActivityAdminLoadingState,
  slugify,
  tagStyle,
  type TagOption,
  type ActivitySummary,
} from "./ActivityAdminShared";
import { PROGRAMME_LABELS, type ProgrammeValue } from "@convex/schemaEnums";
import { tagColor } from "@/lib/tagColor";
import { joinList, list, oneOf, searchUpdate, text } from "@/lib/search";
import { FilterCount } from "../components/FilterCount";

const breadcrumbs = [
  { label: "CeMMbase", to: "/" },
  { label: "Activities", to: "/activities" },
  { label: "Activity catalogue" },
];

type TagDraft = {
  key: string;
  name: string;
  description: string;
  color: string;
};

const emptyTagDraft = (): TagDraft => ({
  key: "",
  name: "",
  description: "",
  color: "#00aecc",
});

const TAG_COLOR_SWATCHES = [
  "#00aecc",
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#e11d48",
  "#ea580c",
  "#f59e0b",
  "#16a34a",
  "#0d9488",
  "#475569",
];

const columnHelper = createColumnHelper<ActivitySummary>();

function buildColumns(handlers: {
  onClone: (activity: ActivitySummary) => void;
  onToggleActive: (activity: ActivitySummary) => void;
  onDelete: (activity: ActivitySummary) => void;
}) {
  return [
    columnHelper.accessor("title", {
      header: "Title",
      cell: ({ row }) => (
        <Box>
          <Text fontWeight="semibold">{row.original.title}</Text>
          <Text mt={1} maxW="xs" fontSize="xs" color="fg.muted" lineClamp={1}>{row.original.description || "No description"}</Text>
        </Box>
      ),
    }),
    columnHelper.accessor("taskCount", {
      header: "Steps",
      cell: ({ getValue }) => (
        <Text fontSize="sm" color="fg.muted">
          {getValue()} step{getValue() === 1 ? "" : "s"}
        </Text>
      ),
    }),
    columnHelper.display({
      id: "tags",
      header: "Tags",
      cell: ({ row }) => (
        <HStack flexWrap="wrap" gap={1.5}>
          {row.original.tags.map((tag) => (
            <Badge key={tag.tagId} variant="outline" textTransform="uppercase" letterSpacing="0.15em" style={tagStyle(tag.color)}>
              {tag.name}
            </Badge>
          ))}
          {row.original.tags.length === 0 ? <Text fontSize="xs" color="fg.muted">—</Text> : null}
        </HStack>
      ),
    }),
    columnHelper.accessor("required", {
      header: "Type",
      cell: ({ getValue }) => (
        <Badge variant={getValue() ? "solid" : "subtle"} colorPalette="gray">
          {getValue() ? "Required" : "Optional"}
        </Badge>
      ),
    }),
    // Which programme page it appears on. A dash is "not part of one", which
    // is also what every entry saved before the field existed reads as -- so
    // this column is how an unmarked catalogue shows itself.
    columnHelper.display({
      id: "programme",
      header: "Programme",
      cell: ({ row }) => {
        const programme = row.original.programme ?? null;
        return programme === null
          ? <Text fontSize="xs" color="fg.muted">—</Text>
          : <Badge variant="outline" colorPalette="purple">{PROGRAMME_LABELS[programme]}</Badge>;
      },
    }),
    columnHelper.accessor("isActive", {
      header: "Status",
      cell: ({ getValue }) => (
        <Badge colorPalette={getValue() ? "green" : "gray"} variant={getValue() ? "solid" : "subtle"}>
          {getValue() ? "Active" : "Inactive"}
        </Badge>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: () => <Box textAlign="right">Actions</Box>,
      cell: ({ row }) => {
        const activity = row.original;
        return (
          <HStack justify="flex-end" gap={1}>
            <IconButton asChild aria-label="Edit activity" title="Edit activity" size="xs" variant="ghost">
              <Link to="/activities/admin/catalogue/$activityId" params={{ activityId: String(activity._id) }}>
                <LuPencil />
              </Link>
            </IconButton>
            <IconButton aria-label="Clone activity" title="Clone activity" size="xs" variant="ghost" onClick={() => handlers.onClone(activity)}>
              <LuCopyPlus />
            </IconButton>
            <IconButton
              aria-label={activity.isActive ? "Deactivate activity" : "Activate activity"}
              title={activity.isActive ? "Deactivate activity" : "Activate activity"}
              size="xs"
              variant="ghost"
              onClick={() => handlers.onToggleActive(activity)}
            >
              {activity.isActive ? <LuCircleOff /> : <LuCircleCheck />}
            </IconButton>
            <IconButton aria-label="Delete activity" title="Delete activity" size="xs" variant="ghost" colorPalette="red" onClick={() => handlers.onDelete(activity)}>
              <LuTrash2 />
            </IconButton>
          </HStack>
        );
      },
    }),
  ];
}

/**
 * The catalogue's tags, as filters first and as things to manage second.
 *
 * Small enough to sit above the table rather than below it, where a card per
 * tag pushed them out of sight. Clicking a tag narrows the table to the
 * activities that carry it, and every further tag narrows it again -- all of
 * them, not any: "PhD" and "Committee" is the committee meetings, which is
 * what someone picking two tags is looking for. The pencil beside each opens
 * the same drawer as before, where the tag can also be deleted.
 */
function TagBar({
  tags,
  counts,
  selected,
  onToggle,
  onEdit,
  onCreate,
}: {
  tags: TagOption[];
  counts: Map<string, number>;
  selected: string[];
  onToggle: (tagId: string) => void;
  onEdit: (tag: TagOption) => void;
  onCreate: () => void;
}) {
  return (
    <Flex align="center" gap={2} wrap="wrap">
      {tags.map((tag) => {
        const tagId = String(tag._id);
        return (
          <Group key={tagId} attached>
            <Toggle.Root asChild pressed={selected.includes(tagId)} onPressedChange={() => onToggle(tagId)}>
              <Button
                size="2xs"
                variant="outline"
                colorPalette="gray"
                title={tag.description ?? undefined}
                css={tagChipCss(tag.color)}
              >
                {tag.name}
                <Badge className="tag-count" size="xs" variant="solid" fontVariantNumeric="tabular-nums">
                  {counts.get(tagId) ?? 0}
                </Badge>
              </Button>
            </Toggle.Root>
            <IconButton
              size="2xs"
              variant="outline"
              colorPalette="gray"
              aria-label={`Edit tag ${tag.name}`}
              title="Edit tag"
              css={{ borderColor: tagColor(tag.color), color: tagColor(tag.color) }}
              onClick={() => onEdit(tag)}
            >
              <LuPencil />
            </IconButton>
          </Group>
        );
      })}
      <Button size="2xs" variant="ghost" onClick={onCreate}>
        <LuPlus />
        New tag
      </Button>
    </Flex>
  );
}

// A tag keeps its own colour, outlined at rest and filled while it filters. The
// count is a badge in the same colour, which inverts with the chip so it stays
// readable on the filled one.
function tagChipCss(color: string | null) {
  const ink = tagColor(color);
  return {
    borderColor: ink,
    color: ink,
    "& .tag-count": { background: ink, color: "white" },
    "&[aria-pressed=true]": { background: ink, color: "white", borderColor: ink },
    "&[aria-pressed=true] .tag-count": { background: "white", color: ink },
  };
}

type ProgrammeFilter = "all" | "none" | ProgrammeValue;
type TypeFilter = "all" | "required" | "optional";
type StatusFilter = "all" | "active" | "inactive";

export type CatalogueSearch = {
  q?: string;
  programme?: "none" | ProgrammeValue;
  type?: "required" | "optional";
  status?: "active" | "inactive";
  /** Tag KEYS, not ids: an address somebody reads, keeps and sends. */
  tags?: string;
};

export function validateCatalogueSearch(search: Record<string, unknown>): CatalogueSearch {
  return {
    q: text(search.q),
    programme: oneOf(search.programme, ["none", ...(Object.keys(PROGRAMME_LABELS) as ProgrammeValue[])]),
    type: oneOf(search.type, ["required", "optional"] as const),
    status: oneOf(search.status, ["active", "inactive"] as const),
    tags: joinList(list(search.tags) ?? []),
  };
}

export function ActivityCataloguePage() {
  const access = useViewerAccess();
  const isActivityAdmin = access?.isActivityAdmin ?? false;

  const catalogue = useQuery(api.activities.listAllActivities, isActivityAdmin ? {} : "skip") as ActivitySummary[] | undefined;
  const tags = useQuery(api.activities.listTags, isActivityAdmin ? {} : "skip") as TagOption[] | undefined;

  const [flashMessage, setFlashMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [tagSidebarOpen, setTagSidebarOpen] = useState(false);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [tagDraft, setTagDraft] = useState<TagDraft>(emptyTagDraft());
  const [confirmDeleteActivityId, setConfirmDeleteActivityId] = useState<string | null>(null);
  // Every filter is in the address, so a narrowed catalogue can be linked to
  // and survives a reload.
  const params = useSearch({ from: "/_authed/activities/admin/catalogue" });
  const search = params.q ?? "";
  const programmeFilter: ProgrammeFilter = params.programme ?? "all";
  const typeFilter: TypeFilter = params.type ?? "all";
  const statusFilter: StatusFilter = params.status ?? "all";
  const selectedTagKeys = list(params.tags) ?? [];

  const createTag = useMutation(api.activities.createTag);
  const updateTag = useMutation(api.activities.updateTag);
  const deleteTag = useMutation(api.activities.deleteTag);
  const setActivityActive = useMutation(api.activities.setActivityActive);
  const deleteActivity = useMutation(api.activities.deleteActivity);
  const navigate = useNavigate();
  const navigateHere = useNavigate({ from: "/activities/admin/catalogue" });
  const setParams = (patch: CatalogueSearch) =>
    void navigateHere({ search: (prev) => ({ ...prev, ...patch }), ...searchUpdate });

  function flash(tone: "success" | "error", text: string) {
    setFlashMessage({ tone, text });
    setTimeout(() => setFlashMessage(null), 4000);
  }

  function openCreateTag() {
    setEditingTagId(null);
    setTagDraft(emptyTagDraft());
    setTagSidebarOpen(true);
  }

  function openEditTag(tag: TagOption) {
    setEditingTagId(String(tag._id));
    setTagDraft({
      key: tag.key,
      name: tag.name,
      description: tag.description ?? "",
      color: tag.color ?? "#00aecc",
    });
    setTagSidebarOpen(true);
  }

  async function handleSaveTag() {
    try {
      if (editingTagId) {
        await updateTag({
          tagId: editingTagId as Id<"activityTags">,
          key: tagDraft.key,
          name: tagDraft.name,
          description: tagDraft.description || null,
          color: tagDraft.color || null,
          isActive: true,
        });
        flash("success", "Tag updated.");
      } else {
        await createTag({
          key: tagDraft.key,
          name: tagDraft.name,
          description: tagDraft.description || null,
          color: tagDraft.color || null,
        });
        flash("success", "Tag created.");
      }
      setTagSidebarOpen(false);
      setEditingTagId(null);
      setTagDraft(emptyTagDraft());
    } catch (error) {
      flash("error", error instanceof Error ? error.message : "Saving tag failed.");
    }
  }

  async function handleDeleteTag(tagId: string) {
    try {
      await deleteTag({ tagId: tagId as Id<"activityTags"> });
      setTagSidebarOpen(false);
      setEditingTagId(null);
      // Out of the filter too, rather than left in the address matching nothing.
      const key = tags?.find((tag) => String(tag._id) === tagId)?.key;
      if (key !== undefined && selectedTagKeys.includes(key)) {
        setParams({ tags: joinList(selectedTagKeys.filter((other) => other !== key)) });
      }
      flash("success", "Tag deleted.");
    } catch (error) {
      flash("error", error instanceof Error ? error.message : "Deleting tag failed.");
    }
  }

  function handleCloneActivity(activity: ActivitySummary) {
    void navigate({
      to: "/activities/admin/catalogue/new",
      search: { cloneFrom: String(activity._id) },
    });
  }

  async function handleToggleActive(activity: ActivitySummary) {
    try {
      await setActivityActive({
        activityId: activity._id,
        isActive: !activity.isActive,
      });
      flash("success", `Activity "${activity.title}" ${activity.isActive ? "deactivated" : "activated"}.`);
    } catch (error) {
      flash("error", error instanceof Error ? error.message : "Toggling activity failed.");
    }
  }

  async function handleDeleteActivity() {
    if (!confirmDeleteActivityId) return;
    try {
      await deleteActivity({
        activityId: confirmDeleteActivityId as Id<"activities">,
      });
      flash("success", "Activity deleted.");
    } catch (error) {
      flash("error", error instanceof Error ? error.message : "Deleting activity failed.");
    } finally {
      setConfirmDeleteActivityId(null);
    }
  }

  const activityToDelete = confirmDeleteActivityId
    ? (catalogue ?? []).find((t) => String(t._id) === confirmDeleteActivityId)
    : null;

  if (access === undefined) {
    return <ActivityAdminLoadingState title="Activity catalogue" description="Checking your access to the activity catalogue." breadcrumbs={breadcrumbs} />;
  }

  if (!isActivityAdmin) {
    return <ActivityAdminAccessDenied title="Activity catalogue" description="This page is for activity admins." breadcrumbs={breadcrumbs} />;
  }

  // A tag deleted elsewhere, or a key nobody has, would otherwise filter
  // everything out.
  const filterTagIds = (tags ?? [])
    .filter((tag) => selectedTagKeys.includes(tag.key))
    .map((tag) => String(tag._id));
  const tagCounts = new Map<string, number>();
  for (const activity of catalogue ?? []) {
    for (const tagId of activity.tagIds) {
      tagCounts.set(String(tagId), (tagCounts.get(String(tagId)) ?? 0) + 1);
    }
  }
  const needle = search.trim().toLowerCase();
  const matching = (catalogue ?? []).filter((activity) => {
    if (!filterTagIds.every((tagId) => activity.tagIds.some((own) => String(own) === tagId))) {
      return false;
    }
    const programme = activity.programme ?? null;
    if (programmeFilter === "none" ? programme !== null : programmeFilter !== "all" && programme !== programmeFilter) {
      return false;
    }
    if (typeFilter !== "all" && activity.required !== (typeFilter === "required")) {
      return false;
    }
    if (statusFilter !== "all" && activity.isActive !== (statusFilter === "active")) {
      return false;
    }
    return needle === ""
      || activity.title.toLowerCase().includes(needle)
      || activity.description.toLowerCase().includes(needle);
  });
  const isFiltered = needle !== "" || filterTagIds.length > 0
    || programmeFilter !== "all" || typeFilter !== "all" || statusFilter !== "all";
  function clearFilters() {
    void navigateHere({ search: {}, ...searchUpdate });
  }
  // Active first, then the inactive ones, dimmed: one table, so one header to
  // pin, where two tables would have had two.
  const shown = [...matching.filter((t) => t.isActive), ...matching.filter((t) => !t.isActive)];
  const columns = buildColumns({
    onClone: handleCloneActivity,
    onToggleActive: (t) => void handleToggleActive(t),
    onDelete: (t) => setConfirmDeleteActivityId(String(t._id)),
  });

  return (
    <>
      <Box minH="full" bg="bg.subtle">
        <PageHeader
          title="Activity catalogue"
          description="Every activity that can be assigned. Open one to edit its steps and forms."
          breadcrumbs={breadcrumbs}
          actions={
            <HStack flexWrap="wrap" gap={3}>
              <Badge variant="outline" px={4} py={2} textTransform="uppercase" letterSpacing="0.2em">{catalogue?.length ?? 0} catalogue</Badge>
              <Badge variant="outline" px={4} py={2} textTransform="uppercase" letterSpacing="0.2em">{tags?.length ?? 0} tags</Badge>
              <Button asChild colorPalette="blue">
                <Link to="/activities/admin/catalogue/new" search={{ cloneFrom: undefined }}>
                  <LuPlus />
                  New activity
                </Link>
              </Button>
            </HStack>
          }
        />

        <Stack gap={8} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
          {flashMessage ? (
            <Alert.Root status={flashMessage.tone === "error" ? "error" : "success"}>
              <Alert.Indicator />
              <Alert.Title>{flashMessage.text}</Alert.Title>
            </Alert.Root>
          ) : null}

          {/* ── Catalogue ── */}
          <Box as="section">
            <Stack gap={3} mb={4}>
              <Heading size="lg">Activities</Heading>

              {/* Search and the three properties every entry has, in one row;
                  the tags, which an entry may or may not carry, below it. All
                  of them narrow together. */}
              <Stack direction={{ base: "column", lg: "row" }} gap={3}>
                <InputGroup flex="1" startElement={<Icon as={LuSearch} boxSize={4} color="fg.muted" />}>
                  <Input
                    size="sm"
                    placeholder="Search titles and descriptions"
                    value={search}
                    onChange={(event) => setParams({ q: event.target.value === "" ? undefined : event.target.value })}
                  />
                </InputGroup>
                <NativeSelect.Root size="sm" w={{ base: "full", lg: "12rem" }}>
                  <NativeSelect.Field
                    aria-label="Programme"
                    value={programmeFilter}
                    onChange={(event) => {
                      const value = event.target.value as ProgrammeFilter;
                      setParams({ programme: value === "all" ? undefined : value });
                    }}
                  >
                    <option value="all">All programmes</option>
                    {Object.entries(PROGRAMME_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                    <option value="none">No programme</option>
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
                <NativeSelect.Root size="sm" w={{ base: "full", lg: "10rem" }}>
                  <NativeSelect.Field
                    aria-label="Type"
                    value={typeFilter}
                    onChange={(event) => {
                      const value = event.target.value as TypeFilter;
                      setParams({ type: value === "all" ? undefined : value });
                    }}
                  >
                    <option value="all">All types</option>
                    <option value="required">Required</option>
                    <option value="optional">Optional</option>
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
                <NativeSelect.Root size="sm" w={{ base: "full", lg: "10rem" }}>
                  <NativeSelect.Field
                    aria-label="Status"
                    value={statusFilter}
                    onChange={(event) => {
                      const value = event.target.value as StatusFilter;
                      setParams({ status: value === "all" ? undefined : value });
                    }}
                  >
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Stack>

              {tags === undefined ? null : (
                <TagBar
                  tags={tags}
                  counts={tagCounts}
                  selected={filterTagIds}
                  onToggle={(tagId) => {
                    const key = tags.find((tag) => String(tag._id) === tagId)?.key;
                    if (key === undefined) return;
                    setParams({
                      tags: joinList(selectedTagKeys.includes(key)
                        ? selectedTagKeys.filter((other) => other !== key)
                        : [...selectedTagKeys, key]),
                    });
                  }}
                  onEdit={openEditTag}
                  onCreate={openCreateTag}
                />
              )}
            </Stack>

            {catalogue === undefined ? (
              <LoadingCard label="Loading the catalogue…" />
            ) : catalogue.length === 0 ? (
              <Card.Root borderWidth="1px" borderStyle="dashed" borderColor="border">
                <Card.Body alignItems="center" py={12} textAlign="center">
                  <Text fontSize="sm" color="fg.muted">No catalogue yet. Create your first activity to get started.</Text>
                </Card.Body>
              </Card.Root>
            ) : (
              // No overflow on this box: the pinned header sticks to the page's
              // own scroll, and an ancestor with overflow would clip it.
              <Stack gap={2}>
                <FilterCount
                  shown={shown.length}
                  total={catalogue.length}
                  noun={["activity", "activities"]}
                  filtered={isFiltered}
                  onClear={clearFilters}
                />
              <Box borderWidth="1px" borderColor="border" shadow="sm" bg="bg">
                <DataTable
                  data={shown}
                  columns={columns}
                  getRowId={(row) => String(row._id)}
                  getRowProps={(row) => (row.isActive ? {} : { opacity: 0.6 })}
                  emptyMessage="No activity matches these filters."
                  stickyHeader
                  interactive={false}
                />
              </Box>
              </Stack>
            )}
          </Box>
        </Stack>
      </Box>

      {/* ── Tag sidebar ── */}
      <DrawerSidebar
        open={tagSidebarOpen}
        onClose={() => setTagSidebarOpen(false)}
        title={editingTagId ? "Edit tag" : "Create tag"}
        subtitle="Tag management"
        icon={<Icon as={LuTag} boxSize={5} />}
        description="Tags group activities, so they are easier to find."
        footer={
          <HStack gap={3} w="full">
            <Button colorPalette="blue" flex="1" onClick={() => void handleSaveTag()}>
              <LuSave />
              {editingTagId ? "Update tag" : "Create tag"}
            </Button>
            {/* Here rather than beside the tag: the bar keeps one control per
                tag. The server refuses while any activity still carries it. */}
            {editingTagId ? (
              <IconButton
                aria-label="Delete tag"
                title="Delete tag (only while no activity carries it)"
                variant="outline"
                colorPalette="red"
                onClick={() => void handleDeleteTag(editingTagId)}
              >
                <LuTrash2 />
              </IconButton>
            ) : null}
            <Button variant="outline" onClick={() => setTagSidebarOpen(false)}>
              Cancel
            </Button>
          </HStack>
        }
      >
        <Stack gap={5} p={6}>
          <Field.Root>
            <Field.Label>Name</Field.Label>
            <Input
              placeholder="e.g. Onboarding"
              value={tagDraft.name}
              onChange={(e) => {
                const name = e.target.value;
                setTagDraft((d) => ({
                  ...d,
                  name,
                  key: editingTagId ? d.key : slugify(name),
                }));
              }}
            />
          </Field.Root>
          <Field.Root>
            <Field.Label>Description</Field.Label>
            <Textarea
              minH={24}
              placeholder="What is this tag used for?"
              value={tagDraft.description}
              onChange={(e) => setTagDraft((d) => ({ ...d, description: e.target.value }))}
            />
          </Field.Root>
          <Field.Root>
            <Field.Label>Color</Field.Label>
            <ColorPicker.Root
              value={parseColor(tagDraft.color || "#00aecc")}
              onValueChange={(e) => setTagDraft((d) => ({ ...d, color: e.value.toString("hex") }))}
            >
              <ColorPicker.HiddenInput />
              <ColorPicker.Control>
                <ColorPicker.Input flex="1" fontFamily="mono" fontSize="sm" />
                <ColorPicker.Trigger>
                  <ColorPicker.ValueSwatch boxSize={5} rounded="sm" />
                </ColorPicker.Trigger>
              </ColorPicker.Control>
              <Portal>
                <ColorPicker.Positioner>
                  <ColorPicker.Content>
                    <ColorPicker.Area>
                      <ColorPicker.AreaBackground />
                      <ColorPicker.AreaThumb />
                    </ColorPicker.Area>
                    <HStack>
                      <ColorPicker.EyeDropper size="xs" variant="outline" />
                      <ColorPicker.ChannelSlider channel="hue" flex="1">
                        <ColorPicker.ChannelSliderTrack />
                        <ColorPicker.ChannelSliderThumb />
                      </ColorPicker.ChannelSlider>
                    </HStack>
                    <ColorPicker.SwatchGroup>
                      {TAG_COLOR_SWATCHES.map((swatch) => (
                        <ColorPicker.SwatchTrigger key={swatch} value={swatch}>
                          <ColorPicker.Swatch value={swatch}>
                            <ColorPicker.SwatchIndicator>
                              <LuCheck />
                            </ColorPicker.SwatchIndicator>
                          </ColorPicker.Swatch>
                        </ColorPicker.SwatchTrigger>
                      ))}
                    </ColorPicker.SwatchGroup>
                  </ColorPicker.Content>
                </ColorPicker.Positioner>
              </Portal>
            </ColorPicker.Root>
          </Field.Root>

          {/* Preview */}
          <Box borderWidth="1px" borderColor="border" bg="bg" p={4}>
            <Text mb={2} fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">Preview</Text>
            <Badge variant="outline" px={4} py={2} textTransform="uppercase" letterSpacing="0.18em" style={tagStyle(tagDraft.color)}>
              {tagDraft.name || "Tag name"}
            </Badge>
          </Box>
        </Stack>
      </DrawerSidebar>

      {/* ── Delete activity confirmation modal ── */}
      <Dialog.Root open={Boolean(confirmDeleteActivityId)} onOpenChange={(e) => { if (!e.open) setConfirmDeleteActivityId(null); }} role="alertdialog">
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxW="md">
              <Dialog.Header>
                <Dialog.Title color="red.500">Delete activity</Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body>
                <Text>
                  Are you sure you want to permanently delete{" "}
                  <Text as="strong" fontWeight="bold">{activityToDelete?.title ?? "this activity"}</Text>? This action cannot be undone.
                </Text>
              </Dialog.Body>
              <Dialog.Footer>
                <ButtonGroup gap={2}>
                  <Button variant="ghost" onClick={() => setConfirmDeleteActivityId(null)}>Cancel</Button>
                  <Button colorPalette="red" onClick={() => void handleDeleteActivity()}>
                    <LuTrash2 />
                    Delete permanently
                  </Button>
                </ButtonGroup>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
}
